import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createBulletinComment,
  deleteBulletinComment,
  downloadBulletinCommentAttachment,
  fetchBulletinComments,
} from '../../api/bulletinComment';
import { BulletinComment } from '../../types/bulletinComment';
import './BulletinComments.css';

import { reportError } from '../../utils/notify';
interface BulletinCommentsProps {
  postId: number;
}

function BulletinComments({ postId }: BulletinCommentsProps) {
  const [comments, setComments] = useState<BulletinComment[]>([]);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setComments(await fetchBulletinComments(postId));
      setError('');
    } catch {
      reportError(setError, '댓글을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments]);

  const handleSubmit = async () => {
    if (!content.trim() && files.length === 0) {
      reportError(setError, '내용 또는 첨부파일을 추가해 주세요.');
      return;
    }
    try {
      setSubmitting(true);
      await createBulletinComment(postId, content, files);
      setContent('');
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setError('');
      await load();
    } catch {
      reportError(setError, '댓글 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment: BulletinComment) => {
    if (!comment.canDelete) return;
    if (!window.confirm('이 댓글을 삭제할까요?')) return;
    try {
      await deleteBulletinComment(postId, comment.commentId);
      await load();
    } catch {
      reportError(setError, '댓글 삭제에 실패했습니다.');
    }
  };

  return (
    <section className="bulletin-comments">
      <div className="bulletin-comments__head">
        <div>
          <h3>댓글</h3>
          <span>의견과 첨부파일을 남길 수 있습니다</span>
        </div>
        <em>{comments.length}개</em>
      </div>

      <div className="bulletin-comments__list" ref={listRef}>
        {loading ? (
          <p className="bulletin-comments__hint">불러오는 중...</p>
        ) : comments.length === 0 ? (
          <p className="bulletin-comments__hint">아직 댓글이 없습니다. 첫 의견을 남겨 보세요.</p>
        ) : (
          comments.map((comment) => (
            <article key={comment.commentId} className="bulletin-comments__item">
              <header>
                <strong>{comment.createdUserName || '사용자'}</strong>
                <time>{formatDateTime(comment.createdAt)}</time>
                {comment.canDelete ? (
                  <button
                    type="button"
                    className="bulletin-comments__delete"
                    onClick={() => void handleDelete(comment)}
                  >
                    삭제
                  </button>
                ) : null}
              </header>
              <p className="bulletin-comments__content">{comment.content}</p>
              {comment.attachments && comment.attachments.length > 0 ? (
                <ul className="bulletin-comments__files">
                  {comment.attachments.map((file) => (
                    <li key={file.attachmentId}>
                      <button
                        type="button"
                        onClick={() =>
                          void downloadBulletinCommentAttachment(
                            postId,
                            comment.commentId,
                            file.attachmentId,
                            file.fileName
                          )
                        }
                      >
                        {file.fileName}
                        {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </article>
          ))
        )}
      </div>

      <div className="bulletin-comments__composer">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="댓글을 입력하세요"
        />
        {files.length > 0 ? (
          <ul className="bulletin-comments__pending">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`}>
                <span>
                  {file.name} ({formatSize(file.size)})
                </span>
                <button type="button" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}>
                  제거
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="bulletin-comments__composer-actions">
          <label className="bulletin-comments__attach">
            파일 첨부
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => {
                const next = Array.from(e.target.files ?? []);
                setFiles((prev) => [...prev, ...next].slice(0, 10));
                e.target.value = '';
              }}
            />
          </label>
          <button type="button" disabled={submitting} onClick={() => void handleSubmit()}>
            {submitting ? '등록 중...' : '댓글 등록'}
          </button>
        </div>
      </div>

      {error ? <p className="bulletin-comments__error">{error}</p> : null}
    </section>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSize(size: number) {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

export default BulletinComments;
